import { effect, inject, Injectable, signal } from '@angular/core'
import { LeaderboardClient, LeaderboardData, LeaderboardGoal, PracticesClient } from '../shared/services/ppv.api'
import { combineLatest, forkJoin, map, take, tap } from 'rxjs'
import { LeaderboardPractice } from './models'
import { parseISO, addDays } from 'date-fns'

@Injectable({ providedIn: 'root' })
export class LeaderboardService {
   private practiceClient = inject(PracticesClient)
   private leaderboardClient = inject(LeaderboardClient)

   practices = signal<LeaderboardPractice[]>([])
   currentPracticeData = signal<LeaderboardData>(undefined)
   currentPracticeDataScans = signal<LeaderboardData>(undefined)

   scansStats = signal<LeaderboardPractice[]>(undefined)

   // This can possibly go away or moved when we allow different contest selection.
   // For now, pick the goal/contest in the current time range, otherwise the previous one, otherwise the first one.
   private _getGoalsMap(goals: LeaderboardGoal[]): Map<number, LeaderboardGoal> {
      const now = new Date()
      const goalsMap = new Map<number, LeaderboardGoal>()

      for (const goal of goals) {
         const enrichedGoal = {
            ...goal,
            startDate: new Date(goal.startDate),
            endDate: new Date(goal.endDate),
         }

         const key = enrichedGoal.practiceSKey
         const existing = goalsMap.get(key)

         if (!existing) {
            goalsMap.set(key, enrichedGoal)
            continue
         }

         const isCurrent = enrichedGoal.startDate <= now && enrichedGoal.endDate >= now
         const isExistingCurrent = existing.startDate <= now && existing.endDate >= now

         if (isCurrent && !isExistingCurrent) {
            goalsMap.set(key, enrichedGoal)
            continue
         }

         if (!isCurrent && !isExistingCurrent) {
            const isBeforeNow = enrichedGoal.startDate < now
            const isExistingBeforeNow = existing.startDate < now

            if (isBeforeNow && isExistingBeforeNow) {
               if (enrichedGoal.startDate > existing.startDate) {
                  goalsMap.set(key, enrichedGoal)
               }
               continue
            }

            if (isBeforeNow && !isExistingBeforeNow) {
               goalsMap.set(key, enrichedGoal)
               continue
            }

            if (!isBeforeNow && !isExistingBeforeNow) {
               if (enrichedGoal.startDate < existing.startDate) {
                  goalsMap.set(key, enrichedGoal)
               }
            }
         }
      }

      return goalsMap
   }

   _getLeaderboardPracticesFromGoalsAndStats(practices: any[], goals: LeaderboardGoal[], stats: LeaderboardData[]) {
      const goalsMap = this._getGoalsMap(goals)
      const statsMap = new Map(stats.map((stat) => [stat.practiceSKey, stat]))
      const practicesWithGoals = practices.filter((p) => goalsMap.has(p.practiceSKey))

      return practicesWithGoals.map((practice) => {
         const firstHm = practice.hMs.length > 0 ? practice.hMs[0] : undefined

         return {
            ...practice,
            goal: goalsMap.get(practice.practiceSKey),
            stats: statsMap.get(practice.practiceSKey),
            hmFirstName: firstHm?.firstName,
            hmLastName: firstHm?.lastName,
            isCanada: practice.state == 'AB' || practice.state == 'ON' ? true : false,
            lastProcessDate: parseISO((practice.lastProcessDate as unknown as string) + 'Z', {}),
         }
      }) as LeaderboardPractice[]
   }

   loadScansStats(contestNumber: number) {
      this.scansStats.set([])
      forkJoin([this.leaderboardClient.getGoals(contestNumber), this.leaderboardClient.getStats(contestNumber)])
         .pipe(
            map(([goals, stats]) => this._getLeaderboardPracticesFromGoalsAndStats(this.practices(), goals, stats)),
            tap((practices) => {
               this.scansStats.set(practices)
            })
         )
         .subscribe()
   }

   loadPractices(contestNumber: number) {
      forkJoin([
         this.practiceClient.searchPractices({ includeClosedPractices: false }),
         this.leaderboardClient.getGoals(contestNumber),
         this.leaderboardClient.getStats(contestNumber),
      ])
         .pipe(
            map(([practices, goals, stats]) => this._getLeaderboardPracticesFromGoalsAndStats(practices, goals, stats)),
            take(1)
         )
         .subscribe((practices) => {
            this.practices.set(practices)
         })
   }

   loadPractice(practiceSKey: number, contestNumber: number, contestStartDate: Date, contestEndDate?: Date, target: 'pets' | 'scans' = 'pets') {
      if (!practiceSKey || !contestStartDate) {
         target === 'pets' ? this.currentPracticeData.set(undefined) : this.currentPracticeDataScans.set(undefined)
         return
      }

      const practice = this.practices()?.find((p) => p.practiceSKey === practiceSKey)
      let startDate: Date | undefined
      let endDate: Date | undefined

      if (contestNumber === 3) {
         startDate = target === 'scans' && practice?.goal?.startDate ? addDays(practice.goal.startDate, -28) : contestStartDate
         endDate = target === 'scans' && practice?.goal?.endDate ? addDays(practice.goal.endDate, 7) : undefined
      } else {
         startDate = target === 'scans' && practice?.goal?.startDate ? addDays(practice.goal.startDate, 7) : contestStartDate
         endDate = target === 'scans' && practice?.goal?.endDate ? addDays(practice.goal.endDate, 7) : undefined
      }

      target === 'pets' ? this.currentPracticeData.set(undefined) : this.currentPracticeDataScans.set(undefined)

      // IF NEEDED: To remove the added 7 days for scans chart, remove endDate param and change startDate to contestStartDate
      this.leaderboardClient
         .getPracticeStats(practiceSKey, startDate, endDate ? endDate : undefined)
         .pipe(
            map((data) => ({
               ...data,
               weeklyData: data?.weeklyData?.map((wd) => ({
                  ...wd,
                  startOfWeek: new Date(wd.startOfWeek as unknown as string),
               })),
               dailyData: data?.dailyData?.map((dd) => ({
                  ...dd,
                  date: new Date(dd.date as unknown as string),
               })),
            }))
         )
         .pipe(
            tap((data) => {
               target === 'pets' ? this.currentPracticeData.set(data) : this.currentPracticeDataScans.set(data)
            })
         )
         .subscribe()
   }
}
